import { 
  Role, 
  User, 
  PermissionMatrix, 
  ModulePermission,
  UpdateRolePermissionsRequest,
  UpdateUserPermissionsRequest,
  UserWithRole
} from '../../../shared/types';

interface Module {
  ModuleKey: string;
  ModuleName: string;
}

class PermissionService {
  private baseUrl = '/api/v1/permissions';

  private getAuthHeaders() {
    const token = localStorage.getItem('quanly_truonghoc_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }

  // Get all modules
  async getModules(): Promise<Module[]> {
    const response = await fetch(`${this.baseUrl}/modules`, {
      headers: this.getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch modules');
    }
    
    return response.json();
  }

  // Get all roles
  async getRoles(): Promise<Role[]> {
    const response = await fetch(`${this.baseUrl}/roles`, {
      headers: this.getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch roles');
    }
    
    return response.json();
  }

  // Get users with roles
  async getUsersWithRoles(): Promise<UserWithRole[]> {
    const response = await fetch(`${this.baseUrl}/users`, {
      headers: this.getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch users');
    }
    
    return response.json();
  }

  // Get role permissions
  async getRolePermissions(roleId: number): Promise<PermissionMatrix[]> {
    const response = await fetch(`${this.baseUrl}/roles/${roleId}/permissions`, {
      headers: this.getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch role permissions');
    }
    
    return response.json();
  }

  // Get user permissions (including role comparison)
  async getUserPermissions(userId: number): Promise<PermissionMatrix[]> {
    const response = await fetch(`${this.baseUrl}/users/${userId}/permissions`, {
      headers: this.getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch user permissions');
    }
    
    return response.json();
  }

  // Update role permissions
  async updateRolePermissions(roleId: number, permissions: ModulePermission[]): Promise<{ success: boolean; message: string }> {
    const requestBody: UpdateRolePermissionsRequest = { permissions };
    
    const response = await fetch(`${this.baseUrl}/roles/${roleId}/permissions`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(requestBody)
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to update role permissions');
    }
    
    return response.json();
  }

  // Update user permissions
  async updateUserPermissions(userId: number, permissions: ModulePermission[]): Promise<{ success: boolean; message: string }> {
    const requestBody: UpdateUserPermissionsRequest = { permissions };
    
    const response = await fetch(`${this.baseUrl}/users/${userId}/permissions`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(requestBody)
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to update user permissions');
    }
    
    return response.json();
  }

  // Reset user permissions to default (role-based only)
  async resetUserPermissions(userId: number): Promise<{ success: boolean; message: string }> {
    const response = await fetch(`${this.baseUrl}/users/${userId}/permissions`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to reset user permissions');
    }
    
    return response.json();
  }
}

export const permissionService = new PermissionService();
export default permissionService; 